import { createContext } from 'reka-ui'
import {
  type SearchContextMetadata,
  type SearchContextType
} from './global-search-utils'
import { type ComputedRef, type Ref } from 'vue'
import { GatTextEdit } from '@gat-ship/vue-components'
import { type SearchResult, type RecentSearch } from './search-types'

export interface SearchProviderContext {
  /** The default search context. */
  defaultSearchContext: SearchContextType
  /** The selected search context. */
  selectedSearchContext: ComputedRef<SearchContextType>
  /** The allowed search contexts. */
  allowedSearchContexts: ComputedRef<
    Partial<Record<SearchContextType, boolean>>
  >
  /** The allowed search contexts metadata. */
  allowedSearchContextsMetadata: ComputedRef<
    Partial<Record<SearchContextType, SearchContextMetadata>>
  >
  /** Set the selected search context. */
  setSelectedSearchContext: (context: SearchContextType) => void
  /** The input value. */
  searchInputValue: Ref<string>
  /** The input component reference. */
  searchInputRef: Ref<InstanceType<typeof GatTextEdit> | null>
  /** The last search value. */
  lastSearchValue: Ref<string | undefined>
  /** Whether the search is currently searching. */
  isSearching: ComputedRef<boolean>
  /** Whether the search is dirty. */
  isSearchInputDirty: ComputedRef<boolean>
  /** Whether the search is empty. */
  isSearchInputEmpty: ComputedRef<boolean>
  /** Whether the search input is focused. */
  isSearchInputFocused: ComputedRef<boolean>
  /** Call the search. */
  callSearch: (
    search: string,
    context: SearchContextType
  ) => Promise<SearchResult[] | undefined>

  /** The search results.
   * undefined when the search is not finished.
   * [] when the search is finished and there are no results.
   * [SearchResultItem...] when the search is finished and there are results.
   */
  searchResults: Ref<SearchResult[] | undefined>
  /** Whether the search results are empty. */
  isEmptySearchResults: ComputedRef<boolean>
  /** Set the search results. */
  setSearchResults: (result: SearchResult[]) => void
  /** Clear the search results. */
  clearSearchResults: () => void
  /** Clear the search. */
  clearSearch: () => void
}

export const [useSearch, provideSearch] =
  createContext<SearchProviderContext>('gs-search-provider')

export interface RecentSearchProviderContext {
  /** The recent search items. */
  recentSearchItems: ComputedRef<RecentSearch[]>
  /** Clear the recent search items. */
  clearRecentSearches: () => void
  /** Add a recent search. */
  addRecentSearch: (recentSearch: RecentSearch) => void
}

export const [useRecentSearch, provideRecentSearch] =
  createContext<RecentSearchProviderContext>('gs-recent-search-provider')
